import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { Check, Minus, Plus, Search } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { formatMoney } from './TotalsPanel';
import { calcLine } from '../../pages/quotes/quoteUtils';
import './product-search.css';

const CURRENCY_CODES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];

const emptyCustom = () => ({
  name: '',
  sku: '',
  price: '',
  costPrice: '',
  weight: '',
  imageUrl: '',
});

/** Map a catalog product into a fully-populated quote line. */
function productToLine(product, configuration = {}) {
  const price = Number(configuration.price ?? product.price) || 0;
  const costRaw = product.costPrice ?? product.cost;
  const costPrice = costRaw != null && costRaw !== '' ? Number(costRaw) : price;
  return calcLine({
    id: `tmp-${product.productId}-${Date.now()}`,
    productId: product.productId != null ? String(product.productId) : undefined,
    sku: product.sku || undefined,
    name: product.name || 'Product',
    imageUrl: product.imageUrl || product.thumbnailUrl || undefined,
    options:
      configuration.options && Object.keys(configuration.options).length
        ? configuration.options
        : undefined,
    qty: configuration.qty || 1,
    price,
    costPrice,
    discountedPrice: Number(configuration.discountedPrice ?? price),
    isCustom: false,
  });
}

function getProductOptions(product) {
  return Array.isArray(product?.options)
    ? product.options.filter((option) => option?.name && Array.isArray(option.choices))
    : [];
}

function calculateConfiguredPrice(product, selectedOptions) {
  const basePrice = Number(product?.price) || 0;
  return getProductOptions(product).reduce((price, option) => {
    const selected = selectedOptions[option.name];
    const choice = option.choices.find((item) => item.text === selected);
    if (!choice) return price;
    const modifier = Number(choice.priceModifier) || 0;
    if (choice.priceModifierType === 'PERCENT') {
      return price + basePrice * (modifier / 100);
    }
    return price + modifier;
  }, basePrice);
}

const ProductSearch = ({
  currency = 'USD',
  onCurrencyChange,
  onAdd,
  showCurrencySelector = true,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState(emptyCustom());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [discountedPrice, setDiscountedPrice] = useState('');

  const clearSearch = () => {
    setQuery('');
    setResults([]);
  };

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const data = await api.searchProducts(term);
      setResults(data?.products || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (e) => {
    const term = e.target.value;
    setQuery(term);
    if (term.trim().length >= 1) {
      search(term);
    } else {
      setResults([]);
    }
  };

  const openProduct = (product) => {
    setSelectedProduct(product);
    setSelectedOptions({});
    setQuantity(1);
    setDiscountedPrice(String(Number(product.price) || 0));
  };

  const closeProduct = () => {
    setSelectedProduct(null);
    setSelectedOptions({});
    setQuantity(1);
    setDiscountedPrice('');
  };

  const chooseOption = (option, choice) => {
    const next = { ...selectedOptions, [option.name]: choice.text };
    setSelectedOptions(next);
    setDiscountedPrice(String(calculateConfiguredPrice(selectedProduct, next)));
  };

  const addConfiguredProduct = () => {
    if (!selectedProduct) return;
    const missing = getProductOptions(selectedProduct).find(
      (option) => option.required && !selectedOptions[option.name],
    );
    if (missing) return;
    const configuredPrice = calculateConfiguredPrice(selectedProduct, selectedOptions);
    onAdd?.(
      productToLine(selectedProduct, {
        options: selectedOptions,
        qty: quantity,
        price: configuredPrice,
        discountedPrice:
          discountedPrice === '' ? configuredPrice : Math.max(0, Number(discountedPrice) || 0),
      }),
    );
    closeProduct();
    clearSearch();
  };

  const onCustomImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCustom((prev) => ({ ...prev, imageUrl: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const createCustom = () => {
    if (!custom.name.trim()) return;
    const price = parseFloat(custom.price) || 0;
    const cost =
      custom.costPrice !== '' && custom.costPrice != null
        ? parseFloat(custom.costPrice) || 0
        : price;
    onAdd?.(
      calcLine({
        id: `custom-${Date.now()}`,
        name: custom.name.trim(),
        sku: custom.sku.trim() || undefined,
        imageUrl: custom.imageUrl || undefined,
        qty: 1,
        price,
        costPrice: cost,
        discountedPrice: price,
        weight: custom.weight !== '' ? parseFloat(custom.weight) || 0 : undefined,
        isCustom: true,
      }),
    );
    setCustom(emptyCustom());
    setShowCustom(false);
    clearSearch();
  };

  const configuredPrice = selectedProduct
    ? calculateConfiguredPrice(selectedProduct, selectedOptions)
    : 0;
  const productOptions = getProductOptions(selectedProduct);
  const missingRequiredOption = productOptions.some(
    (option) => option.required && !selectedOptions[option.name],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 text-uppercase text-muted">{t('productSearch.addProducts')}</h6>
        <Button size="sm" variant="warning" className="text-white" onClick={() => setShowCustom(true)}>
          <Plus size={14} className="me-1" />
          {t('productSearch.newCustomProduct')}
        </Button>
      </div>

      {showCurrencySelector ? (
        <Form.Group className="mb-3" style={{ maxWidth: 280 }}>
          <Form.Label>{t('productSearch.selectCurrency')}</Form.Label>
          <Form.Select value={currency} onChange={(e) => onCurrencyChange?.(e.target.value)}>
            {CURRENCY_CODES.map((code) => (
              <option key={code} value={code}>
                {t(`productSearch.currencies.${code}`, { defaultValue: code })}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      ) : null}

      <div className="position-relative mb-3">
        <Form.Control
          placeholder={t('productSearch.searchPlaceholder')}
          value={query}
          onChange={handleQueryChange}
        />
        <Search size={16} className="position-absolute text-muted" style={{ right: 12, top: 10 }} />
      </div>

      {loading && <Spinner size="sm" animation="border" className="mb-2" />}

      {results.length > 0 && (
        <div className="table-responsive border rounded mb-3 bg-white">
          <Table hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 64 }}>{t('productSearch.image')}</th>
                <th>{t('productSearch.name')}</th>
                <th>{t('productSearch.sku')}</th>
                <th style={{ width: 120 }}>{t('productSearch.price')}</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {results.map((p) => (
                <tr key={p.productId}>
                  <td>
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt=""
                        width={40}
                        height={40}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="bg-light border rounded" style={{ width: 40, height: 40 }} />
                    )}
                  </td>
                  <td className="fw-semibold">{p.name}</td>
                  <td className="text-muted">{p.sku || '—'}</td>
                  <td>{formatMoney(p.price, currency)}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" onClick={() => openProduct(p)}>
                      {t('productSearch.select')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={Boolean(selectedProduct)}
        onHide={closeProduct}
        centered
        size="xl"
        dialogClassName="q2c-product-picker-dialog"
      >
        {selectedProduct ? (
          <>
            <Modal.Header closeButton className="q2c-product-picker-head">
              <div className="q2c-product-picker-summary">
                <div className="q2c-product-picker-image">
                  {selectedProduct.imageUrl ? (
                    <img src={selectedProduct.imageUrl} alt={selectedProduct.name} />
                  ) : (
                    <div className="q2c-product-picker-placeholder" />
                  )}
                </div>
                <div>
                  <Modal.Title>{selectedProduct.name}</Modal.Title>
                  {selectedProduct.sku ? (
                    <div className="q2c-product-picker-sku">{t('productSearch.skuLabel', { sku: selectedProduct.sku })}</div>
                  ) : null}
                  <div className="q2c-product-picker-price">
                    {formatMoney(configuredPrice, currency)}
                  </div>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="q2c-product-picker-body">
              {productOptions.length ? (
                <div className="q2c-product-options">
                  {productOptions.map((option) => (
                    <fieldset key={option.name} className="q2c-product-option">
                      <legend>
                        {option.name}
                        {option.required ? <span aria-label="required"> *</span> : null}
                      </legend>
                      <div className="q2c-product-choices">
                        {option.choices.map((choice) => {
                          const active = selectedOptions[option.name] === choice.text;
                          const isSwatch =
                            option.type === 'SWATCHES' && choice.hexCodes?.length > 0;
                          return (
                            <button
                              key={choice.text}
                              type="button"
                              className={`q2c-product-choice${active ? ' is-active' : ''}`}
                              onClick={() => chooseOption(option, choice)}
                              aria-pressed={active}
                            >
                              {isSwatch ? (
                                <span
                                  className="q2c-product-swatch"
                                  style={{
                                    background:
                                      choice.hexCodes.length > 1
                                        ? `linear-gradient(135deg, ${choice.hexCodes.join(', ')})`
                                        : choice.hexCodes[0],
                                  }}
                                />
                              ) : null}
                              <span>{choice.text}</span>
                              {Number(choice.priceModifier) !== 0 ? (
                                <small>
                                  {Number(choice.priceModifier) > 0 ? '+' : ''}
                                  {choice.priceModifierType === 'PERCENT'
                                    ? `${choice.priceModifier}%`
                                    : formatMoney(choice.priceModifier, currency)}
                                </small>
                              ) : null}
                              {active ? <Check size={14} /> : null}
                            </button>
                          );
                        })}
                      </div>
                    </fieldset>
                  ))}
                </div>
              ) : (
                <div className="q2c-product-no-options">
                  {t('productSearch.noOptions')}
                </div>
              )}

              <div className="q2c-product-picker-fields">
                <Form.Group>
                  <Form.Label>{t('productSearch.quantity')}</Form.Label>
                  <div className="q2c-product-quantity">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                      disabled={quantity <= 1}
                      aria-label={t('productSearch.decreaseQuantity')}
                    >
                      <Minus size={15} />
                    </Button>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      value={quantity}
                      onChange={(event) =>
                        setQuantity(Math.max(1, parseInt(event.target.value, 10) || 1))
                      }
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => setQuantity((value) => value + 1)}
                      aria-label={t('productSearch.increaseQuantity')}
                    >
                      <Plus size={15} />
                    </Button>
                  </div>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('productSearch.discountedItemPrice')}</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountedPrice}
                    onChange={(event) => setDiscountedPrice(event.target.value)}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer className="q2c-product-picker-footer">
              {missingRequiredOption ? (
                <span className="q2c-product-required-hint">
                  {t('productSearch.selectRequiredOptions')}
                </span>
              ) : null}
              <Button variant="secondary" onClick={closeProduct}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="warning"
                className="text-white"
                onClick={addConfiguredProduct}
                disabled={missingRequiredOption || quantity < 1}
              >
                {t('productSearch.addToQuote')}
              </Button>
            </Modal.Footer>
          </>
        ) : null}
      </Modal>

      <Modal show={showCustom} onHide={() => setShowCustom(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('productSearch.newCustomProduct')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label>{t('productSearch.productImage')}</Form.Label>
              <div
                className="border border-dashed rounded d-flex flex-column align-items-center justify-content-center p-3 bg-light"
                style={{ minHeight: 160 }}
              >
                {custom.imageUrl ? (
                  <img src={custom.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: 120 }} />
                ) : (
                  <span className="text-muted small">{t('productSearch.uploadImage')}</span>
                )}
                <Form.Control type="file" accept="image/*" className="mt-2" onChange={onCustomImage} />
              </div>
            </div>
            <div className="col-md-8">
              <Form.Group className="mb-3">
                <Form.Label>{t('productSearch.productName')}</Form.Label>
                <Form.Control
                  placeholder={t('productSearch.productName')}
                  value={custom.name}
                  onChange={(e) => setCustom((p) => ({ ...p, name: e.target.value }))}
                />
              </Form.Group>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('productSearch.sku')}</Form.Label>
                    <Form.Control
                      placeholder={t('productSearch.sku')}
                      value={custom.sku}
                      onChange={(e) => setCustom((p) => ({ ...p, sku: e.target.value }))}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('productSearch.price')}</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={t('productSearch.price')}
                      value={custom.price}
                      onChange={(e) => setCustom((p) => ({ ...p, price: e.target.value }))}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('productSearch.costPrice')}</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={t('productSearch.costPrice')}
                      value={custom.costPrice}
                      onChange={(e) => setCustom((p) => ({ ...p, costPrice: e.target.value }))}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('productSearch.weight')}</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={t('productSearch.weight')}
                      value={custom.weight}
                      onChange={(e) => setCustom((p) => ({ ...p, weight: e.target.value }))}
                    />
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustom(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="warning" className="text-white" onClick={createCustom} disabled={!custom.name.trim()}>
            {t('productSearch.createProduct')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductSearch;
